import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";

/**
 * Returns history items sorted descending by start date.
 */
export const sortHistoryItemsByStartDateDesc = <
  T extends { startDate: string | null | undefined },
>(
  items: T[],
): T[] => {
  return [...items].sort((left, right) => {
    const leftValue = left.startDate ?? "";
    const rightValue = right.startDate ?? "";
    if (leftValue === rightValue) return 0;
    return leftValue < rightValue ? 1 : -1;
  });
};

/**
 * Deletion is blocked only when the history row start date equals the
 * employment start date (same calendar day). For missing/invalid dates,
 * deletion is allowed.
 */
export const canDeleteHistoryItemForEmploymentStartDate = (
  historyStartDate: string | null,
  employmentStartDateIso: string | null,
): boolean => {
  if (employmentStartDateIso == null) return true;

  const historyDatePart =
    parseIsoDateToLocalDateOrNull(historyStartDate)?.toString() ?? null;
  if (historyDatePart == null) return true;

  const employmentDatePart =
    parseIsoDateToLocalDateOrNull(employmentStartDateIso)?.toString() ?? null;
  if (employmentDatePart == null) return true;

  return historyDatePart !== employmentDatePart;
};

/**
 * Allows deletion for items dated from one month before today and forward.
 * Example: if today is 2026-05-07, earliest deletable date is 2026-04-01.
 */
export const canDeleteHistoryItemWithinLastMonthOrFuture = (
  historyStartDate: string | null,
): boolean => {
  const historyDate = parseIsoDateToLocalDateOrNull(historyStartDate);
  if (historyDate == null) return true;

  const thresholdDate = getStockholmLocalDateNow()
    .minusMonths(1)
    .withDayOfMonth(1);

  return !historyDate.isBefore(thresholdDate);
};
